<script setup>
import dayjs from 'dayjs'
import { getI18n } from '@/plugins/i18n'
import { ref, computed, onMounted } from 'vue'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useScheduleStore } from '@/stores/schedule.store'
import { planningApi } from '@/apis/planning.api'
import { scheduleApi } from '@/apis/schedule.api'

import { useNotify } from '@/composables/notify'
import { useNavigator } from '@/composables/navigator'
import { useAllocationAvailability } from '@/composables/allocation-availability'
import { useAllocationConfirm } from '@/composables/allocation-confirm'
import { useHighlightNewRow } from '@/composables/scroll'
import { useChips } from '@/composables/chips'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ListItem from '@/components/shared/ListItem.vue'
import ServiceListItem from '@/components/shared/ServiceListItem.vue'
import DescriptionSection from '@/components/shared/DescriptionSection.vue'
import ProjectAttachmentsSection from '@/components/shared/ProjectAttachmentsSection.vue'
import ServiceAllocationControls from '@/components/shared/ServiceAllocationControls.vue'
import AllocationConfirmDialog from '@/components/planner/AllocationConfirmDialog.vue'
import NoStockConfirmDialog from '@/components/shared/NoStockConfirmDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import SearchInput from '@/components/shared/SearchInput.vue'

const i18n = getI18n()
const { notifySuccess } = useNotify()
const { loadAvailabilityAtProjectStart, buildAllocationsFromAvailability } = useAllocationAvailability()
const { noStockDialog, runConfirmAsync } = useAllocationConfirm()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { getProjectTagChips } = useChips()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const scheduleStore = useScheduleStore()

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  // Extra actions absorbed into the header dots-menu (e.g. from a sibling component).
  menuActions: {
    type: Array,
    default: () => [],
  },
  showEditProjectButton: {
    type: Boolean,
    default: true,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  hideProjectInfo: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['cancel', 'update', 'edit', 'change-status', 'delivery-note', 'archive'])

const projectItem = ref(props.modelValue)
const services = ref([])
const servicesContainer = ref(null)
const serviceFilter = ref('')

const serviceRowKey = (item) => item.budget_document_row_id ?? item.id
const listingServiceIds = ref(null)
const allocationConfirmItem = ref(null)
const deleteConfirmItem = ref(null)
const showUnassignDialog = computed({
  get: () => deleteConfirmItem.value !== null,
  set: (val) => { if (!val) deleteConfirmItem.value = null },
})
const unlockRangePending = ref(null)
// Bumped to force the q-range controls to re-initialise from props (e.g. snap back on cancel).
const rangeResetToken = ref(0)

useNavigator(() => {
  if (!allocationConfirmItem.value) emit('cancel')
})

const refreshServicesAsync = async () => {
  services.value = (await planningApi.getProjectServicesByProjectIdAsync(projectItem.value.id)).data ?? []
}

onMounted(async () => {
  await refreshServicesAsync()
  const fetches = []
  if (planningStore.staff.length === 0) fetches.push(planningStore.fetchStaffsAsync({ all: true }, true))
  if (planningStore.equipments.length === 0) fetches.push(planningStore.fetchEquipmentsAsync({ all: true }, true))
  if (planningStore.groups.length === 0) fetches.push(planningStore.fetchGroupsAsync({ all: true }, true))
  await Promise.all(fetches)
  if (projectItem.value.listing) {
    const items = await planningStore.fetchListingItemsAsync({ listing: projectItem.value.listing, all: true })
    listingServiceIds.value = new Set((items.data || items || []).map(i => i.service))
  }
})

// The endpoint emits the rows in budget-row order (each root followed by its children).
const rowName = (item) => item.listing_item_text || item.service_name || ''

const availableServices = computed(() => {
  const assigned = new Set(services.value.map(s => s.id))
  let list = planningStore.getServices().filter(s => !assigned.has(s.id))
  if (listingServiceIds.value) {
    list = list.filter(s => listingServiceIds.value.has(s.id))
  }
  if (serviceFilter.value) {
    const lower = serviceFilter.value.toLowerCase()
    list = list.filter(s => s.name?.toLowerCase().includes(lower))
  }
  return list
})

const avatarIcon = (item) => item.service_type === portalStore.serviceTypeEnum.GROUP ? 'mdi-select-group' : undefined

// Assigning a service mirrors the timeline drag&drop: it only creates the planned
// allocation (sync-range) — it does NOT confirm the warehouse allocation.
const onServiceAdd = async (serviceId) => {
  if (!serviceId) return
  const previousKeys = new Set(services.value.map(serviceRowKey))
  await scheduleStore.syncRangeAsync(serviceId, projectItem.value.id, projectItem.value.start, projectItem.value.end, {
    quantity: 1,
    listingId: projectItem.value.listing,
  })
  notifySuccess(i18n.t('info.assignedSuccessfully'))
  await refreshServicesAsync()
  emit('update')
  const newService = services.value.find(s => !previousKeys.has(serviceRowKey(s)))
  await highlightRowAsync(newService ? serviceRowKey(newService) : null, servicesContainer)
}

// Resizing a confirmed allocation must first prompt to unlock it (same as the timeline).
const onAllocationRangeUpdate = async ({ item, start, end }) => {
  if (item.confirmed) {
    unlockRangePending.value = { item, start, end }
    return
  }
  await applyRangeUpdate(item, start, end)
}

const applyRangeUpdate = async (item, start, end) => {
  await scheduleStore.updateRangeAsync(projectItem.value.id, start, end, {
    serviceId: item.id,
    budgetDocumentRowId: item.budget_document_row_id,
    updatePlanned: true,
  })
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await refreshServicesAsync()
  emit('update')
}

const onConfirmUnlockAndResize = async () => {
  const pending = unlockRangePending.value
  unlockRangePending.value = null
  if (!pending) return
  await scheduleApi.cancelAllocationAsync(pending.item.id, projectItem.value.id, pending.item.budget_document_row_id)
  await applyRangeUpdate(pending.item, pending.start, pending.end)
}

const onCancelUnlock = () => {
  unlockRangePending.value = null
  // Snap the q-range back to the stored (unchanged) value.
  rangeResetToken.value++
}

const onUnassignClick = (item) => {
  deleteConfirmItem.value = item
}

const onConfirmUnassign = async () => {
  const item = deleteConfirmItem.value
  deleteConfirmItem.value = null
  if (item.budget_document_row_id) {
    await scheduleStore.deleteRangeAsync(item.budget_document_row_id)
  } else {
    await scheduleApi.deleteRangeAsync(null, { serviceId: item.id, projectId: projectItem.value.id })
  }
  notifySuccess(i18n.t('info.unassignedSuccessfully'))
  await refreshServicesAsync()
  emit('update')
}

const isSerializedOrStaff = (serviceId) => {
  const svc = planningStore.getServiceById(serviceId)
  return svc?.is_serialized || svc?.type === portalStore.serviceTypeEnum.STAFF
}

// Confirm behaves like the timeline: staff/serialized or a single available unit
// are auto-confirmed; only a real multi-warehouse choice opens the dialog.
const onConfirmAllocation = async (item) => {
  if (item.confirmed) {
    await scheduleApi.cancelAllocationAsync(item.id, projectItem.value.id, item.budget_document_row_id)
    await refreshServicesAsync()
    emit('update')
    return
  }

  if (isSerializedOrStaff(item.id)) {
    runConfirmAsync(
      force => scheduleApi.confirmAllocationAsync(item.id, projectItem.value.id, [], item.budget_document_row_id, force, { skipErrorMessage: true }),
      async () => { await refreshServicesAsync(); emit('update') },
    )
    return
  }

  try {
    const availability = await loadAvailabilityAtProjectStart([item.id], item.start ?? projectItem.value.start)
    const allocations = buildAllocationsFromAvailability(availability, item.id)
    const totalAvailable = allocations.reduce((sum, a) => sum + a.available, 0)
    if (totalAvailable === 1) {
      const single = allocations
        .filter(a => a.available > 0)
        .map(a => ({ warehouse_id: a.warehouseId, quantity: 1 }))
      await scheduleApi.confirmAllocationAsync(item.id, projectItem.value.id, single, item.budget_document_row_id)
      await refreshServicesAsync()
      emit('update')
      return
    }
  } catch (err) {
    console.error('Failed to resolve allocation availability', err)
  }

  allocationConfirmItem.value = item
}

const onAllocationConfirmed = async () => {
  allocationConfirmItem.value = null
  await refreshServicesAsync()
  emit('update')
}

const onCancel = () => emit('cancel')
</script>

<template>
  <q-card class="dialog-card-flex">
    <navigate-back
      v-if="!props.hideHeader"
      :text="projectItem.name"
      :icon="showEditProjectButton ? (projectItem.status !== planningStore.projectStatusEnum.PLANNED ? 'mdi-magnify' : 'mdi-pencil') : null"
      :archived="projectItem.archived"
      @cancel="onCancel"
      @click="emit('edit')"
    >
      <template #right>
        <div style="display: flex; align-items: center;">
          <q-btn
            v-if="!projectItem.archived"
            flat
            round
            icon="mdi-pencil"
            @click="emit('edit')"
          />
          <q-btn flat round icon="mdi-dots-vertical">
            <q-menu anchor="bottom right" self="top right">
              <q-list style="min-width: 12.5rem">
                <q-item
                  v-close-popup
                  clickable
                  @click="emit('delivery-note')"
                >
                  <q-item-section avatar><q-icon name="mdi-printer" /></q-item-section>
                  <q-item-section>{{ i18n.t('common.printDeliveryNote') }}</q-item-section>
                </q-item>
                <template v-if="menuActions.length">
                  <q-item
                    v-for="action in menuActions"
                    :key="action.key"
                    v-close-popup
                    clickable
                    @click="action.handler()"
                  >
                    <q-item-section avatar><q-icon :name="action.icon" /></q-item-section>
                    <q-item-section>{{ action.label }}</q-item-section>
                  </q-item>
                  <q-separator />
                </template>
                <q-item
                  v-if="!projectItem.archived"
                  v-close-popup
                  clickable
                  @click="emit('change-status')"
                >
                  <q-item-section avatar><q-icon name="mdi-swap-horizontal" /></q-item-section>
                  <q-item-section>{{ i18n.t('common.status') }}</q-item-section>
                </q-item>
                <q-separator />
                <q-item
                  v-close-popup
                  clickable
                  @click="emit('archive')"
                >
                  <q-item-section avatar><q-icon :name="projectItem?.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'" /></q-item-section>
                  <q-item-section>{{ i18n.t(projectItem?.archived ? 'common.unarchive' : 'common.archive') }}</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
      </template>
    </navigate-back>
    <template v-if="!hideProjectInfo">
      <q-card-section class="q-pt-none">
        <q-list>
          <q-item>
            <div style="width: 50%; float: left;">
              <q-item-section>
                <q-item-label caption>
                  {{ i18n.t('common.period') }}
                </q-item-label>
                <q-item-label class="align-items-baseline">
                  <q-icon name="mdi-clock-outline" class="q-mr-xs" />
                  <span>
                    {{ dayjs(projectItem.start).format('HH:mm') }} - {{ dayjs(projectItem.end).format('HH:mm') }}
                  </span>
                </q-item-label>
                <q-item-label class="align-items-baseline">
                  <q-icon name="mdi-calendar" class="q-mr-xs" />
                  <span>
                    {{ dayjs(projectItem.start).format('DD/MM') }} to {{ dayjs(projectItem.end).format('DD/MM') }}
                  </span>
                </q-item-label>
              </q-item-section>
            </div>
            <div style="width: 50%; float: right;">
              <q-item-section style="display: flex; align-content: flex-end;">
                <q-item-label caption style="text-align: right;">
                  {{ i18n.t('common.status') }}
                </q-item-label>
                <q-item-label>
                  <chip-template
                    :values="[planningStore.getLocalizedProjectStatuses().find(i => i.id === projectItem.status)]"
                    :style="{ 'margin-right': '0' }"
                  />
                </q-item-label>
              </q-item-section>
            </div>
          </q-item>
          <q-item v-if="projectItem.tag_ids?.length">
            <q-item-section>
              <q-item-label caption>
                {{ i18n.t('common.tag', 2) }}
              </q-item-label>
              <q-item-label>
                <chip-template
                  :values="getProjectTagChips(projectItem.tag_ids)"
                />
              </q-item-label>
            </q-item-section>
          </q-item>
          <description-section v-if="projectItem.description" :text="projectItem.description" />
          <project-attachments-section v-if="projectItem.project_files?.length" :files="projectItem.project_files" />
        </q-list>
      </q-card-section>
      <q-separator />
    </template>
    <div v-if="!projectItem.archived" style="flex-shrink: 0;">
      <q-card-section class="q-pt-none q-pb-none" :class="hideProjectInfo ? 'q-px-sm' : ''">
        <q-list>
          <q-expansion-item
            expand-icon="mdi-plus-circle-outline"
            expanded-icon="mdi-minus-circle-outline"
            dense
            dense-toggle
            @hide="serviceFilter = ''"
          >
            <template #header>
              <q-item-section>
                <q-item-label caption>{{ i18n.t('common.assign') }}</q-item-label>
              </q-item-section>
            </template>
            <div class="q-pa-sm">
              <search-input v-model="serviceFilter" />
              <q-separator class="q-my-sm" />
              <div style="max-height: 40vh; overflow-y: auto;">
                <service-list-item
                  v-for="svc in availableServices"
                  :key="svc.id"
                  :service="svc"
                  clickable
                  dense
                  @click="onServiceAdd(svc.id)"
                />
                <div v-if="!availableServices.length" class="text-grey text-caption q-py-sm q-px-sm">
                  {{ i18n.t('common.noResults') }}
                </div>
              </div>
            </div>
          </q-expansion-item>
        </q-list>
      </q-card-section>
      <q-separator />
    </div>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll q-pb-md">
      <q-card-section class="q-pt-none q-pb-none" :class="hideProjectInfo ? 'q-px-sm' : ''">
        <q-list>
          <q-item class="q-pt-sm q-pb-xs">
            <q-item-section>
              <q-item-label caption>{{ i18n.t('common.service', 2) }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>
      <q-card-section ref="servicesContainer" class="q-pt-none q-px-md">
        <template v-if="services.length">
          <list-item
            v-for="item in services"
            :key="serviceRowKey(item)"
            class="q-mb-md"
            :class="{ 'row-highlight': serviceRowKey(item) === highlightedRowId }"
            :avatar="item.service_avatar_file || item.service_avatar_url"
            :avatar-icon="avatarIcon(item)"
          >
            <template #label>
              <q-item-label>{{ rowName(item) }}</q-item-label>
              <service-allocation-controls
                v-if="!projectItem.archived"
                :key="`${item.id}-${item.start}-${item.end}-${rangeResetToken}`"
                :service="item"
                :project-start="projectItem.start"
                :project-end="projectItem.end"
                :color="item.confirmed ? projectItem.color : null"
                class="q-mx-md"
                @update-range="(range) => onAllocationRangeUpdate({ item, ...range })"
              />
            </template>
            <template #right>
              <q-item-section
                v-if="!projectItem.archived"
                avatar
                class="align-middle align-center q-px-none"
                style="flex-direction: row; gap: 4px;"
              >
                <q-icon
                  :name="item.confirmed ? 'mdi-check-circle' : 'mdi-check-circle-outline'"
                  color="positive"
                  size="sm"
                  class="cursor-pointer q-mr-sm"
                  @click.stop="onConfirmAllocation(item)"
                >
                  <q-tooltip>{{ i18n.t(item.confirmed ? 'common.cancelAllocation' : 'common.confirmAllocation') }}</q-tooltip>
                </q-icon>
                <q-icon
                  name="mdi-delete-outline"
                  color="negative"
                  size="sm"
                  class="cursor-pointer"
                  @click.stop="onUnassignClick(item)"
                >
                  <q-tooltip>{{ i18n.t('common.unassign') }}</q-tooltip>
                </q-icon>
              </q-item-section>
            </template>
          </list-item>
        </template>
        <div v-else class="text-grey text-caption q-py-sm">
          {{ i18n.t('common.noResults') }}
        </div>
      </q-card-section>
    </q-card-section>
  </q-card>
  <confirm-dialog
    v-model="showUnassignDialog"
    :message="i18n.t('common.confirmDelete')"
    icon="mdi-delete-outline"
    icon-color="negative"
    @confirm="onConfirmUnassign"
  />
  <q-dialog :model-value="unlockRangePending !== null" persistent class="dialog-xs">
    <q-card>
      <q-card-section>
        <div class="text-h6">
          <q-icon name="mdi-lock-alert" color="warning" class="q-mr-sm" />
          {{ i18n.t('common.confirmAllocation') }}
        </div>
      </q-card-section>
      <q-card-section>
        <div>{{ i18n.t('common.confirmedAllocationsBlockMove') }}</div>
        <q-list v-if="unlockRangePending" dense class="q-mt-sm">
          <q-item dense>
            <q-item-section avatar>
              <q-icon name="mdi-check-circle" color="positive" size="sm" />
            </q-item-section>
            <q-item-section>{{ unlockRangePending.item.service_name }}</q-item-section>
          </q-item>
        </q-list>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat :label="i18n.t('common.cancel')" @click="onCancelUnlock" />
        <q-btn color="warning" :label="i18n.t('common.confirm')" @click="onConfirmUnlockAndResize" />
      </q-card-actions>
    </q-card>
  </q-dialog>
  <allocation-confirm-dialog
    v-if="allocationConfirmItem"
    :model-value="true"
    :service-id="allocationConfirmItem.id"
    :project-id="projectItem.id"
    :budget-document-row-id="allocationConfirmItem.budget_document_row_id"
    :service-name="allocationConfirmItem.service_name"
    :project-start="projectItem.start"
    @update:model-value="(val) => { if (!val) allocationConfirmItem = null }"
    @confirmed="onAllocationConfirmed"
  />
  <no-stock-confirm-dialog v-model="noStockDialog" />
</template>

<style scoped>
</style>
